import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamsService } from '../services/teamsService';
import type { TeamQuery, CreateTeamRequest, UpdateTeamRequest } from '@troia-v3/types';

export type { TeamDropdownItem } from '../services/teamsService';

/**
 * Hook para buscar lista de teams com paginação e filtros
 */
export const useTeams = (query?: TeamQuery) => {
  return useQuery({
    queryKey: ['teams', query],
    queryFn: () => teamsService.getTeams(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook para buscar team por ID
 */
export const useTeamData = (id: string | undefined) => {
  return useQuery({
    queryKey: ['teams', id],
    queryFn: () => teamsService.getTeamById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para criar team
 */
export const useCreateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTeamRequest) => teamsService.createTeam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

/**
 * Hook para atualizar team
 */
export const useUpdateTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTeamRequest }) =>
      teamsService.updateTeam(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['teams', variables.id] });
    },
  });
};

/**
 * Hook para deletar team
 */
export const useDeleteTeam = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => teamsService.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });
};

/**
 * Hook para ativar/desativar team
 */
export const useToggleTeamStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      status === 'active'
        ? teamsService.activateTeam(id)
        : teamsService.deactivateTeam(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['teams', variables.id] });
    },
  });
};

/**
 * Hook para buscar teams para dropdown
 *
 * `enabled` (default `true`) — condicionar a `isOpen` em modais/drawers
 * (CROSS_MODULE_PERMISSIONS) evita disparar a query com o componente fechado.
 *
 * A chave é hierárquica (`['teams','dropdown']`) de propósito: os
 * `invalidateQueries({ queryKey: ['teams'] })` de criar/editar/excluir equipe
 * a alcançam por prefixo. Mutations de MEMBRO não entram nessa lista: as
 * "oficiais" (`useAddUserToTeam`/`useRemoveUserFromTeam`) invalidam só
 * `['teams', teamId]`; quem usa o prefixo largo `['teams']` é `useTeamUsers.ts`
 * (`useAddTeamUser`/`useRemoveTeamUser`), uma implementação paralela duplicada
 * das duas mesmas mutations. Inofensivo hoje porque o dropdown não expõe
 * membros — mas não conte com invalidação de membro alcançando este cache.
 */
export const useTeamsDropdown = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['teams', 'dropdown'],
    queryFn: () => teamsService.getTeamsForDropdown(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: options?.enabled ?? true,
  });
};
